import { Component, effect, HostBinding, input } from '@angular/core';
import { SharedUiModule } from '@pf/shared-ui';
import { IconType, StatusStyle } from '@pf/shared-common';
import { NzSpinModule } from 'ng-zorro-antd/spin';

@Component({
    standalone: true,
    selector: 'cpw-action-button',
    template: `
        @if (loading()) {
            <nz-spin nzSimple class="action-chip-spinner"></nz-spin>
        } @else {
            <pf-icon
                [iconType]="actionIcon()"
                class="action-chip-icon"></pf-icon>
        }
        <div class="action-chip-label-container">
            <span class="action-chip-label">{{ actionLabel() }}</span>
            <span class="action-chip-description">{{
                actionDescription()
            }}</span>
        </div>
        @if (!loading()) {
            <pf-icon
                size="md"
                [rotate]="90"
                iconType="collapse"
                class="action-chip-button"></pf-icon>
        }
    `,
    styles: [
        `
            :host {
                display: grid;
                grid-template-columns: 50px 1fr 50px;
                grid-template-rows: auto;
                gap: var(--pf-gap);
                align-items: center;
                border: 1px solid var(--ant-primary-color-outline);
                border-radius: 4rem;
                padding: 0.8rem var(--pf-gap);
                margin: var(--pf-gap);
                transition:
                    transform 0.1s ease-in-out,
                    filter 0.1s ease-in-out;

                &:active {
                    transform: scale(0.98);
                    filter: brightness(0.95);
                }

                &:hover {
                    transform: scale(1.02);
                    filter: brightness(1.02);
                }

                &.action-chip-loading {
                    pointer-events: none;
                    opacity: 0.7;
                }

                .action-chip-spinner {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: var(--pf-gap);
                }

                .action-chip-icon {
                    font-size: 1.75rem;
                    padding: var(--pf-gap);
                }

                .action-chip-label-container {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;

                    .action-chip-label {
                        font-weight: bold;
                        font-size: 0.8rem;
                    }

                    .action-chip-description {
                        color: var(--pf-primary-font-color);
                        font-size: smaller;
                    }
                }

                .action-chip-button {
                    border-radius: 5rem;
                    color: white;
                    height: 1.5rem;
                    width: 1.5rem;
                    justify-content: center;
                }

                /*| 'primary'*/
                /*| 'secondary'*/
                /*| 'warning'*/
                /*| 'danger'*/
                /*| 'info'*/
                /*| 'success';*/

                &.action-chip-primary {
                    color: var(--ant-primary-color);
                    border-color: var(--ant-primary-color-outline);
                    background-color: var(--ant-primary-color-outline);

                    .action-chip-button {
                        background-color: var(--ant-primary-color);
                    }
                }

                &.action-chip-secondary {
                    color: var(--pf-secondary-color);
                    border-color: var(--pf-secondary-color-outline);
                    background-color: var(--pf-secondary-color-outline);

                    .action-chip-button {
                        background-color: var(--pf-secondary-color);
                    }
                }

                &.action-chip-warning {
                    color: var(--ant-warning-color);
                    border-color: var(--ant-warning-color-outline);
                    background-color: var(--ant-warning-color-outline);

                    .action-chip-button {
                        background-color: var(--ant-warning-color);
                    }
                }

                &.action-chip-danger {
                    color: var(--ant-error-color);
                    border-color: var(--ant-error-color-outline);
                    background-color: var(--ant-error-color-outline);

                    .action-chip-button {
                        background-color: var(--ant-error-color);
                    }
                }

                &.action-chip-info {
                    color: var(--ant-info-color);
                    border-color: var(--ant-info-color);
                    background-color: var(--ant-info-color);

                    .action-chip-button {
                        background-color: var(--ant-info-color);
                    }
                }

                &.action-chip-success {
                    color: var(--ant-success-color);
                    border-color: var(--ant-success-color-outline);
                    background-color: var(--ant-success-color-outline);

                    .action-chip-button {
                        background-color: var(--ant-success-color);
                    }
                }
            }
        `,
    ],
    imports: [SharedUiModule, NzSpinModule],
})
export class CpwActionButtonComponent {
    @HostBinding('class')
    styleClass?: string;

    actionLabel = input.required<string>();
    actionDescription = input.required<string>();
    actionIcon = input.required<keyof typeof IconType>();
    statusStyle = input<StatusStyle>('primary');
    loading = input<boolean>(false);

    constructor() {
        effect(() => {
            const classes = [`action-chip-${this.statusStyle()}`];
            if (this.loading()) {
                classes.push('action-chip-loading');
            }
            this.styleClass = classes.join(' ');
        });
    }
}
